
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, CheckCircle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface EmailConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

export const EmailConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  userEmail 
}: EmailConfirmationDialogProps) => {
  const [isResending, setIsResending] = useState(false);
  const { toast } = useToast();

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Email resent",
        description: "Check your inbox for the new confirmation email from contact@snaprefund.com.",
      });
    } catch (error: any) {
      console.error("Error resending email:", error);
      toast({
        title: "Failed to resend email",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose} data-gtm="email-confirmation-modal">
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Check Your Email</DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Confirmation link sent from SnapRefund
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Email sent to:</strong> {userEmail}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              <strong>From:</strong> contact@snaprefund.com
            </p>
          </div>
          
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Click the confirmation link in your email</p>
              <p className="text-xs text-gray-600 mt-1">
                You'll be automatically redirected to your dashboard once confirmed
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Note:</strong> Check your spam folder if you don't see the email from contact@snaprefund.com. 
              The link expires in 1 hour.
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={handleResendEmail}
              disabled={isResending}
              variant="outline"
              className="flex-1"
            >
              {isResending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Resend Email"
              )}
            </Button>
            <Button 
              onClick={onClose}
              className="flex-1"
              variant="default"
            >
              Got it, I'll check my email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
